import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { fromEvent, merge } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AutomationHelpers } from 'app/automations/automation-helpers';
import {
  ACTIONS_TYPES,
  ACTIONS_TYPES_REAL_TIME,
  ActionRealTimeScl,
  ActionWithRealTime,
  ALWAYS_REAL_TIME_APPS,
  CONFIG_EDITOR_AUTOMATION,
  PROPS_TYPE,
} from 'app/automations/automation-helpers-actions';
import {
  ActionConfig,
  Automation,
  AutomationEditorConfig,
  CustomModelAction,
  CustomModelProperty,
  TypeStep,
} from 'app/automations/automations-models';
import { IntegrationSettings, IntegrationType } from 'app/automations/integrations-models';
import WidgetAutomationHelpers from 'app/campaign/widget-automation-helpers';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { CampaignStatus } from 'app/campaign/campaign.model';
import { FormSettings, Widget, WidgetState } from 'app/campaign/widgets.model';
import { AccountLanguageService } from 'app/core/account-language.service';
import { WidgetAutomationsDataService } from 'app/campaign/widget-automations-data.service';
import { WidgetAutomationsService } from 'app/campaign/widget-automations.service';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { Account, CustomProfileProperty } from 'app/back-office/account.model';
import { CurrentContextService } from 'app/core/current-context.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { IntegrationsService } from 'app/shared/services/integrations.service';
import { WidgetService } from 'app/shared/services/widget.service';
import { PeopleService } from 'app/people/people.service';
import { EmailTemplateService } from 'app/email-templates/email-template.service';
import { ConfirmationDialogComponent } from 'app/shared/confirmation-dialog/confirmation-dialog.component';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { AppCardVm } from './model/app-card-vm';
import { ContactMode } from './model/contact-mode';
import { inferContactMode } from './model/contact-mode-inference';
import { fieldMappingPairs, hasHalfFilledFieldMappingPair, isFieldMappingPropName } from './model/field-mapping-pairs';
import { actionHasInvalidFormatProp } from './model/form-settings-validation';
import { deriveIntegrationCapabilities, findUpdateListTarget, isListPropName } from './model/integration-capabilities';
import { isTagProfileSwitch } from './model/tag-step-props';
import { UPDATE_SAFE_ADD_CONTACT_ACTIONS } from './model/update-safe-add-contact-actions';
import { AutomationSerializer } from './services/automation-serializer';
import { FlyoutFormSettingsShowMessageComponent } from './show-message/flyout-form-settings-show-message.component';
import { buildHandlerView, toFormSettingsPayload } from './show-message/model/real-time-handler';
import { widgetHasSubscriptionCheckbox } from './show-message/model/show-message-transforms';

/** Excluded from the engine's app list: Centra/Shopify are handled by the eCommerce registration toggle. */
const DEFERRED_APP_NAMES: ReadonlyArray<string> = [IntegrationType.Centra, IntegrationType.Shopify];

/** Triggerbee-native destinations — always offered, since there's no integration to connect. */
const ALWAYS_AVAILABLE_APP_NAMES: ReadonlyArray<string> = [
  'TriggerbeeSendMail',
  'TriggerbeeTransactionEmail',
  'TriggerbeeExecuteWebhook',
  'TriggerbeeProfile',
];

const TRANSACTION_EMAIL_APP = 'TriggerbeeTransactionEmail';

interface FieldMapProp {
  name?: string;
  value?: unknown;
  listUI?: unknown[];
  isSocialInput?: boolean;
}

interface UpdateListOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-flyout-form-settings-v2',
  templateUrl: './flyout-form-settings-v2.component.html',
  styleUrl: './flyout-form-settings-v2.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    // `height: '*'` animates to the synchronously-measured auto height, avoiding the overshoot of
    // mat-expansion-panel's lazy-content animation.
    trigger('expandReveal', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class FlyoutFormSettingsV2Component implements OnInit {
  @Input() public flyoutOpeningData!: FlyoutOpeningData;

  private readonly widgetService = inject(WidgetService);
  private readonly widgetAutomationsService = inject(WidgetAutomationsService);
  private readonly widgetAutomationsDataService = inject(WidgetAutomationsDataService);
  private readonly widgetEditorDataService = inject(WidgetEditorDataService);
  private readonly integrationsService = inject(IntegrationsService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly peopleService = inject(PeopleService);
  private readonly emailTemplateService = inject(EmailTemplateService);
  private readonly accountLanguageService = inject(AccountLanguageService);
  private readonly serializer = inject(AutomationSerializer);
  private readonly snackBarService = inject(SnackBarService);
  private readonly flyoutService = inject(FlyoutService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  public readonly ContactMode = ContactMode;
  public readonly TypeStep = TypeStep;

  private readonly capabilities = deriveIntegrationCapabilities(CONFIG_EDITOR_AUTOMATION as AutomationEditorConfig[]);

  public readonly loading = signal<boolean>(true);
  public readonly errorLoading = signal<boolean>(false);
  public readonly savingAppName = signal<string | null>(null);
  public readonly expandedAppName = signal<string | null>(null);
  /** List/tag id of the most recent selection; fed to the per-action editors so they reload field options. */
  public readonly proceedRequiredListActions = signal<string | undefined>(undefined);

  /** Full editor config (every integration) from the serializer; the visible lists filter this. */
  private readonly editorApps = signal<AutomationEditorConfig[]>([]);
  private readonly activeIntegrations = signal<IntegrationSettings[]>([]);
  private readonly contactModeByApp = signal<Record<string, ContactMode>>({});
  /** Update mode's own list selection, kept out of the editor config so Add mode can't read or clobber it. */
  private readonly updateListByApp = signal<Record<string, string | string[]>>({});
  private readonly updateListOptions = signal<Record<string, UpdateListOption[]>>({});
  private readonly requestedUpdateListUrls = new Set<string>();
  /** The reused per-action editors mutate their `ngModel` targets in place, notifying no signal. */
  private readonly editorEdits = signal<number>(0);

  public readonly customProfileFields = signal<CustomProfileProperty[]>([]);
  public readonly listsIntegrations = signal<Record<string, unknown>>({});
  public readonly fieldsInOutcomes = signal<Array<{ label: string; group: string; value: string }>>([]);

  /** Stays true until we learn the account has no email templates — gates the TransactionEmail destination. */
  public readonly hasEmailTemplates = signal<boolean>(true);
  public readonly tagsForSelector = signal<string[]>([]);
  private tagsForSelectorLoaded = false;

  public tierAllowsAutomations = false;
  // Translation key, not resolved text.
  private missedInputTooltip = '';
  public readonly canGoToAutomation = signal<boolean>(false);

  private widget: Widget | null = null;
  private hasSclComponent = false;
  private widgetState!: WidgetState;
  private account: Account | null = null;
  private existingAutomation: Automation | null = null;
  private campaignName: string | null = null;
  private campaignStatus!: CampaignStatus;
  public isTemplate = false;

  private removedPropsMapField: FieldMapProp[] = [];
  private listUIName: unknown[] | null = null;
  private listUIValue: unknown[] | null = null;
  private removedVerifyNewSubscriber = false;

  /** Connected, non-deferred integrations enriched with auth state, preserving object identity for the editors. */
  public readonly visibleApps = computed<AutomationEditorConfig[]>(() => {
    const connected = new Set(this.activeIntegrations().map((integration) => this.viewName(integration.type)));
    return this.editorApps().filter((app) => {
      if (DEFERRED_APP_NAMES.includes(app.name)) {
        return false;
      }
      if (app.name === TRANSACTION_EMAIL_APP && !this.hasEmailTemplates()) {
        return false;
      }
      return connected.has(app.name) || ALWAYS_AVAILABLE_APP_NAMES.includes(app.name);
    });
  });

  public readonly configuredApps = computed<AutomationEditorConfig[]>(() =>
    this.visibleApps().filter((app) => app.enabled),
  );
  public readonly nonConfiguredApps = computed<AutomationEditorConfig[]>(() =>
    this.visibleApps().filter((app) => !app.enabled),
  );
  public readonly hasNoApps = computed<boolean>(
    () => !this.loading() && this.configuredApps().length === 0 && this.nonConfiguredApps().length === 0,
  );

  /** One Update-mode app is enough: the trigger gates the whole automation, not just that app's actions. */
  public readonly updatesExistingContact = computed<boolean>(() =>
    this.configuredApps().some(
      (app) => this.supportsFork(app.name) && this.getContactMode(app.name) === ContactMode.Update,
    ),
  );

  /**
   * Saved integrations float to the top; an in-progress draft (`enabled && isDraft`) stays in stable config
   * order during setup and only moves to the top once saved (it becomes `enabled && !isDraft`).
   */
  public readonly orderedApps = computed<AutomationEditorConfig[]>(() => {
    const apps = this.visibleApps();
    const saved = apps.filter((app) => app.enabled && !app.isDraft);
    const rest = apps.filter((app) => !(app.enabled && !app.isDraft));
    return [...saved, ...rest];
  });

  /** View-models the template binds to (one per visible app). Recomputes when the apps or contact mode
   *  change (the helper calls below read those signals); see AppCardVm. */
  public readonly appCards = computed<AppCardVm[]>(() => this.orderedApps().map((app) => this.buildAppCard(app)));

  public readonly expandedSaveState = computed<{ canSave: boolean; blockedTooltipKey: string | null }>(() => {
    this.editorEdits();
    const app = this.expandedApp();
    if (!app) {
      return { canSave: false, blockedTooltipKey: null };
    }
    return { canSave: this.canSave(app), blockedTooltipKey: this.saveBlockedTooltipKey(app) };
  });

  public ngOnInit(): void {
    this.campaignName = this.flyoutOpeningData?.campaignName ?? null;
    this.campaignStatus = this.flyoutOpeningData?.campaignStatus;
    this.isTemplate = this.flyoutOpeningData?.isTemplate === true;
    const currentContext = this.currentContextService.currentContextBS.getValue();
    this.account = currentContext?.account ?? null;
    this.tierAllowsAutomations = currentContext?.tierAccess?.isAutomationsAllowed === true;
    this.missedInputTooltip = this.widgetEditorDataService.getMissedInputType(this.account);
    this.watchEditorEdits();

    const customerId = this.currentContextService.currentContextBS.getValue()?.customer?.id;
    if (customerId) {
      this.emailTemplateService
        .getByCustomer(customerId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (templates) => this.hasEmailTemplates.set((templates?.length ?? 0) > 0),
          error: () => undefined,
        });
    }

    this.integrationsService.activeIntegrationsBS
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((integrations) => {
        if (integrations) {
          this.activeIntegrations.set(integrations);
          this.buildListsIntegrations(integrations);
          // Integrations can resolve after the automation has loaded; re-apply auth state / custom overrides
          // so error badges and custom names appear.
          this.reapplyIntegrationState();
        }
      });
    if (!this.integrationsService.activeIntegrationsBS.getValue()) {
      this.integrationsService.loadActiveIntegrations();
    }

    this.peopleService
      .getProfileFields()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fields) => {
          if (fields?.length) {
            this.customProfileFields.set(fields);
          }
        },
        error: () => undefined,
      });

    const widgetId = this.flyoutOpeningData?.widget?.id;
    if (!widgetId) {
      this.errorLoading.set(true);
      this.loading.set(false);
      return;
    }
    this.widgetService
      .getWidgetById(widgetId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (widget) => {
          this.widget = widget;
          this.hasSclComponent = widgetHasSubscriptionCheckbox(widget);
          this.fieldsInOutcomes.set(this.buildOutcomeFields(widget));
          this.widgetState = this.pickWidgetState(widget);
          this.loadAutomation();
        },
        error: () => {
          this.errorLoading.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadAutomation(): void {
    const automationId = this.widgetState?.automationId;
    if (this.isTemplate || !automationId) {
      this.applyAutomation(null);
      return;
    }
    this.widgetAutomationsService
      .getAutomationById(automationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (automation) => this.applyAutomation(automation),
        error: (error: HttpErrorResponse) => {
          // 404 → the automation was deleted; fall back to a fresh, unconfigured editor.
          if (error.status === 404) {
            this.widgetState.automationId = undefined;
            this.applyAutomation(null);
          } else {
            this.errorLoading.set(true);
            this.loading.set(false);
          }
        },
      });
  }

  private applyAutomation(automation: Automation | null): void {
    this.existingAutomation = automation;
    // The reused per-action editor (app-action-automation) reads existingAutomation off this shared service
    // for its real-time field-mapping checks; keep it in sync.
    this.widgetAutomationsDataService.existingAutomation = automation;

    const apps = this.serializer.toEditorConfig(automation, this.widget?.states ?? [], this.account as Account);
    this.enrichWithAuthState(apps);
    this.applyCustomIntegrationOverrides(apps);
    this.applyRealTimeStepDisabling(apps);
    this.editorApps.set(apps);
    this.initContactModes(apps);
    this.refreshGoToAutomation();
    this.loading.set(false);
  }

  // Re-apply integration-derived state (auth/error, custom name/icon) onto the already-built editor config —
  // used when active integrations arrive after the automation has been parsed.
  private reapplyIntegrationState(): void {
    const apps = this.editorApps();
    if (apps.length) {
      this.enrichWithAuthState(apps);
      this.applyCustomIntegrationOverrides(apps);
      this.bumpApps();
    }
  }

  private enrichWithAuthState(apps: AutomationEditorConfig[]): void {
    const integrations = this.activeIntegrations();
    for (const app of apps) {
      const match = integrations.find((integration) => this.viewName(integration.type) === app.name);
      if (match) {
        app.isAuthenticated = match.isAuthenticated;
        app.errorKey = match.errorKey;
      }
    }
  }

  // A connected Custom integration can supply its own display name and icon.
  private applyCustomIntegrationOverrides(apps: AutomationEditorConfig[]): void {
    const customData = this.activeIntegrations().find(
      (integration) => integration.type === IntegrationType.Custom,
    )?.data;
    if (!customData?.name && !customData?.iconUrl) {
      return;
    }
    const customApp = apps.find((app) => app.name === IntegrationType.Custom);
    if (!customApp) {
      return;
    }
    if (customData.name) {
      customApp.displayName = customData.name;
    }
    if (customData.iconUrl) {
      customApp.iconUrl = customData.iconUrl;
    }
  }

  private initContactModes(apps: AutomationEditorConfig[]): void {
    const savedActionTypes = (this.existingAutomation?.actions ?? []).map((action) => action.$type);
    const modes: Record<string, ContactMode> = {};
    for (const app of apps) {
      if (this.supportsFork(app.name)) {
        modes[app.name] = inferContactMode(
          CONFIG_EDITOR_AUTOMATION as AutomationEditorConfig[],
          app.name,
          savedActionTypes,
        );
      }
    }
    this.contactModeByApp.set(modes);
    this.seedUpdateLists(apps, modes);
  }

  /**
   * Apps loading in Add mode deliberately leave the Update picker empty — inheriting the Add list is exactly
   * the cross-mode bleed this fork exists to prevent.
   */
  private seedUpdateLists(apps: AutomationEditorConfig[], modes: Record<string, ContactMode>): void {
    const lists: Record<string, string | string[]> = {};
    for (const app of apps) {
      if (modes[app.name] !== ContactMode.Update) {
        continue;
      }
      const target = findUpdateListTarget(app);
      const value = target ? this.listValueOf(target.actions[0], target.propName) : undefined;
      if (this.hasListValue(value)) {
        lists[app.name] = value;
      }
    }
    this.updateListByApp.set(lists);
  }

  private buildListsIntegrations(integrations: IntegrationSettings[]): void {
    const map: Record<string, unknown> = {};
    for (const integration of integrations) {
      if (integration.data?.listIdsForTrigger) {
        const name = integration.type === IntegrationType.RuleMailer ? IntegrationType.Rule : integration.type;
        map[name.toLowerCase()] = integration.data.listIdsForTrigger;
      }
    }
    this.listsIntegrations.set(map);
  }

  // Outcome custom-data keys become {{session.custom.<key>}} field-mapping sources.
  private buildOutcomeFields(widget: Widget): Array<{ label: string; group: string; value: string }> {
    return (widget.outcomes ?? [])
      .map((outcome) => (outcome as { customDataUpdate?: { key?: string } }).customDataUpdate?.key)
      .filter((key): key is string => !!key)
      .map((key) => ({ label: key, group: 'automations.triggers.groups.contact', value: `{{session.custom.${key}}}` }));
  }

  private pickWidgetState(widget: Widget): WidgetState {
    const withAutomation = widget.states.find((state) => state.automationId);
    if (withAutomation) {
      return withAutomation;
    }
    const withInput = widget.states.find((state) => WidgetComponentHelpers.hasWidgetStateSomeInput(state));
    return withInput ?? this.flyoutOpeningData.widgetState;
  }

  public widgetStatesContext(app: AutomationEditorConfig): {
    widgetState: WidgetState[];
    account: Account | null;
    actionsConfig: unknown;
  } {
    return { widgetState: this.widget?.states ?? [], account: this.account, actionsConfig: app.actionsConfig };
  }

  public toggleExpand(app: AutomationEditorConfig): void {
    if (app.isAuthenticated === false || !!app.errorKey) {
      return;
    }
    if (this.expandedAppName() === app.name) {
      // Collapsing an unsaved, just-enabled app cancels it.
      if (app.isDraft) {
        app.enabled = false;
        app.isDraft = false;
        this.bumpApps();
      }
      this.setExpandedApp(null);
      return;
    }
    this.cancelOtherDrafts(app.name);
    this.setExpandedApp(app.name);
    if (this.hasTagGroups(app)) {
      this.loadTags();
    }
    this.prepareRealTimeForApp(app);
    this.loadUpdateListOptions(app);
    AutomationHelpers.setFieldsMappingActionProp(this.configuredApps());
    this.bumpApps();
  }

  public enableApp(app: AutomationEditorConfig): void {
    if (app.isAuthenticated === false || !!app.errorKey) {
      return;
    }
    this.cancelOtherDrafts(app.name);
    app.enabled = true;
    app.isDraft = true;
    this.setExpandedApp(app.name);
    if (this.hasTagGroups(app)) {
      this.loadTags();
    }
    this.prepareRealTimeForApp(app);
    this.loadUpdateListOptions(app);
    this.bumpApps();
  }

  // Only one in-progress draft at a time: starting/expanding another integration reverts any other
  // just-enabled-but-unsaved app to its not-configured state, so an abandoned draft doesn't linger looking
  // "added" until a reload.
  private cancelOtherDrafts(keepName: string): void {
    for (const app of this.editorApps()) {
      if (app.isDraft && app.name !== keepName) {
        app.enabled = false;
        app.isDraft = false;
      }
    }
  }

  // Force Voyado real-time for VoyadoContactId accounts, strip telephone/SSN from real-time field mappings,
  // and apply the form-mapping flag.
  private prepareRealTimeForApp(app: AutomationEditorConfig): void {
    if (this.account) {
      WidgetAutomationHelpers.forceUseRunningInRealTimeForVoyado(app, this.account);
    }
    if (ALWAYS_REAL_TIME_APPS.includes(app.name)) {
      this.modifyMapFieldRealTime(null, app.name);
      this.widgetAutomationsDataService.isAllowFormMappingPropApplyInitial(app);
    } else if (app.name === IntegrationType.Voyado || app.name === IntegrationType.Rule) {
      this.modifyMapFieldRealTime({ checked: AutomationHelpers.isBrevoDotVoyadoRuleRealTime(app) }, app.name);
    }
  }

  // A subscription checkbox already driving an ESP in real time can't also have its add-subscriber step
  // configured here.
  private applyRealTimeStepDisabling(apps: AutomationEditorConfig[]): void {
    const sclRealTimeActions = (this.existingAutomation?.actions ?? []).filter(
      (action) =>
        (action as { isRunningInRealTime?: boolean }).isRunningInRealTime === true &&
        ActionRealTimeScl.includes(action.$type as ACTIONS_TYPES_REAL_TIME),
    );
    for (const app of apps) {
      for (const group of app.actionsConfig) {
        const action = group.actionsGroup[0]?.action;
        group.disabled =
          !!action?.nameAction &&
          ActionWithRealTime.includes(action.$type as ACTIONS_TYPES) &&
          sclRealTimeActions.some((running) => running.$type === (action as { updatedAction?: string }).updatedAction);
      }
    }
  }

  public supportsFork(appName: string): boolean {
    return this.capabilities.get(appName)?.supportsAddUpdateFork === true;
  }

  /** A non-Voyado integration driven in real time by a subscription checkbox can't be removed here — the SCL
   *  component must be removed first (otherwise it would be left orphaned). */
  public isRealTimeLocked(app: AutomationEditorConfig): boolean {
    return (
      this.hasSclComponent &&
      app.name !== IntegrationType.Voyado &&
      (app as { isRunningInRealTime?: boolean }).isRunningInRealTime === true
    );
  }

  public getContactMode(appName: string): ContactMode {
    return this.contactModeByApp()[appName] ?? ContactMode.Add;
  }

  public setContactMode(appName: string, mode: ContactMode): void {
    this.contactModeByApp.update((modes) => ({ ...modes, [appName]: mode }));
    const app = this.editorApps().find((candidate) => candidate.name === appName);
    if (!app) {
      return;
    }
    this.applyListForMode(app);
    this.loadUpdateListOptions(app);
    this.bumpApps();
  }

  public onUpdateListChange(app: AutomationEditorConfig, value: string | string[] | undefined): void {
    this.updateListByApp.update((lists) => ({ ...lists, [app.name]: value ?? '' }));
    this.applyListForMode(app);
    // Destination field names are scoped to the list, so the per-action editors must reload their options.
    if (this.hasListValue(value)) {
      this.proceedRequiredListActions.set(String(value));
    }
    this.bumpApps();
  }

  /**
   * Add and Update each own their list: Add's comes from the create action's picker, Update's from its own.
   * Re-stamping on every switch is what stops a selection made in one view from satisfying the other.
   */
  private applyListForMode(app: AutomationEditorConfig): void {
    const target = this.supportsFork(app.name) ? findUpdateListTarget(app) : null;
    if (!target) {
      return;
    }
    const isUpdate = this.getContactMode(app.name) === ContactMode.Update;
    const selected = isUpdate ? this.updateListByApp()[app.name] : this.createActionListValue(app, target.propName);
    // Empty stays '' even for a multi-select: the per-action editor gates its "choose a list" error on
    // `!action.tagIds`, and an empty array is truthy, so it would hide the message.
    const value = this.hasListValue(selected) ? selected : '';
    // Both modes, so a toggle cannot strand the tag steps on the other mode's list, which
    // serializeSimpleAction then saves over the value their own dropdown shows.
    [...target.actions, ...target.dependentActions].forEach((action) => {
      (action as unknown as Record<string, unknown>)[target.propName] = value;
    });
  }

  private createActionListValue(app: AutomationEditorConfig, propName: string): string | string[] | undefined {
    const createAction = (this.addContactGroup(app)?.actionsGroup ?? [])
      .map((item) => item.action)
      .find(
        (action) =>
          (action.properties ?? []).some((prop: FieldMapProp) => prop.name === propName) ||
          this.hasListValue(this.listValueOf(action, propName)),
      );
    if (!createAction) {
      return undefined;
    }
    // `prop.value` is only populated once the create action has rendered, which it hasn't while Update mode
    // hides it — fall back to the flattened field so switching to Add can't clear a saved list.
    const prop = (createAction.properties ?? []).find((candidate: FieldMapProp) => candidate.name === propName);
    const fromProp = prop?.value as string | string[] | undefined;
    return this.hasListValue(fromProp) ? fromProp : this.listValueOf(createAction, propName);
  }

  private listValueOf(action: CustomModelAction | undefined, propName: string): string | string[] | undefined {
    return (action as unknown as Record<string, string | string[] | undefined>)?.[propName];
  }

  private hasListValue(value: unknown): boolean {
    return Array.isArray(value) ? value.length > 0 : !!value;
  }

  private loadUpdateListOptions(app: AutomationEditorConfig): void {
    if (this.getContactMode(app.name) !== ContactMode.Update) {
      return;
    }
    const target = findUpdateListTarget(app);
    if (!target || this.requestedUpdateListUrls.has(target.url)) {
      return;
    }
    this.requestedUpdateListUrls.add(target.url);
    this.integrationsService
      .getDataForActionProperties(target.url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: unknown) =>
          this.updateListOptions.update((options) => ({ ...options, [app.name]: this.toListOptions(result) })),
        error: () => this.requestedUpdateListUrls.delete(target.url),
      });
  }

  private toListOptions(result: unknown): UpdateListOption[] {
    const items = (result as { data?: unknown[] })?.data ?? result;
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map((item) => {
      const entry = item as { id?: string; name?: string; displayName?: string };
      const id = entry?.id ?? (item as string);
      return { id, name: entry?.name ?? entry?.displayName ?? id };
    });
  }

  public isGroupVisibleForMode(appName: string, nameGroup: string): boolean {
    if (!this.supportsFork(appName)) {
      return true;
    }
    if (this.getContactMode(appName) === ContactMode.Add) {
      return true;
    }
    return (
      nameGroup === TypeStep.FieldMapping || nameGroup === TypeStep.UpdateOtherFields || nameGroup === TypeStep.Trigger
    );
  }

  public addContactGroup(app: AutomationEditorConfig): ActionConfig | undefined {
    return app.actionsConfig.find((group) => group.nameGroup === TypeStep.AddContact);
  }

  public otherGroups(app: AutomationEditorConfig): ActionConfig[] {
    return app.actionsConfig.filter((group) => group.nameGroup !== TypeStep.AddContact);
  }

  public isTagGroup(nameGroup: string): boolean {
    return nameGroup === TypeStep.TagAdd || nameGroup === TypeStep.TagRemove;
  }

  /** Tracks whether the Zapier embed step's panel is open, so its (heavy, external) widget loads only then. */
  public readonly zapierEmbedExpanded = signal<boolean>(false);

  public isZapierEmbedGroup(group: ActionConfig): boolean {
    return group.nameGroup === TypeStep.ZapierEmbed;
  }

  public onZapierEmbedPanel(opened: boolean): void {
    this.zapierEmbedExpanded.set(opened);
  }

  private hasTagGroups(app: AutomationEditorConfig): boolean {
    return app.actionsConfig.some((group) => this.isTagGroup(group.nameGroup));
  }

  private loadTags(): void {
    if (this.tagsForSelectorLoaded) {
      return;
    }
    this.tagsForSelectorLoaded = true;
    this.widgetService
      .getAllTags()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.tagsForSelector.set(result?.data ?? []),
        error: () => (this.tagsForSelectorLoaded = false),
      });
  }

  public onTagCreated(tag: string): void {
    this.tagsForSelector.update((tags) => [...tags, tag].sort((a, b) => a.localeCompare(b)));
  }

  public onTagStepChange(group: ActionConfig, tag: string | null): void {
    const action = group.actionsGroup[0]?.action as unknown as Record<string, unknown> | undefined;
    if (!action) {
      return;
    }
    action['tag'] = tag;
    const tagProp = (action['properties'] as Array<{ name?: string; value?: unknown }> | undefined)?.find(
      (prop) => prop.name === 'tag',
    );
    if (tagProp) {
      tagProp.value = tag;
    }
    // Refresh the view-model so the tag selector's bound value reflects the change.
    this.bumpApps();
  }

  public selectedTagOf(group: ActionConfig): string | null {
    const action = group.actionsGroup[0]?.action as unknown as { tag?: string | null } | undefined;
    return action?.tag ?? null;
  }

  // Both writes are needed: the serializer flattens `properties`, the card reads the flat field back.
  public onTagProfileSwitchChange(group: ActionConfig, propName: string, checked: boolean): void {
    const action = group.actionsGroup[0]?.action;
    if (!action) {
      return;
    }
    (action as unknown as Record<string, unknown>)[propName] = checked;
    const prop = (action.properties ?? []).find((candidate: CustomModelProperty) => candidate.name === propName);
    if (prop) {
      prop.value = checked;
    }
    this.bumpApps();
  }

  private profileSwitchOf(group: ActionConfig): AppCardVm['groups'][number]['profileSwitch'] {
    const action = group.actionsGroup[0]?.action;
    const prop = (action?.properties ?? []).find((candidate: CustomModelProperty) =>
      isTagProfileSwitch(action, candidate),
    );
    if (!prop) {
      return undefined;
    }
    return {
      propName: prop.name,
      labelKey: `automations.actions.${action.translationKey}.${prop.translationKey}`,
      checked: (action as unknown as Record<string, unknown>)[prop.name] === true,
    };
  }

  public appTranslationName(app: AutomationEditorConfig): string | undefined {
    return (app as { translationName?: string }).translationName;
  }

  public appAdminOnly(app: AutomationEditorConfig): boolean {
    return (app as { adminOnly?: boolean }).adminOnly === true;
  }

  /** Single-step native destinations (Send email / webhook) render their action flat, without a redundant
   *  one-row accordion. Apps with a contact step, multiple steps, or tag steps use the accordion. */
  public rendersFlat(app: AutomationEditorConfig): boolean {
    const groups = this.otherGroups(app);
    return !this.addContactGroup(app) && groups.length === 1 && !this.isTagGroup(groups[0].nameGroup);
  }

  /** Whether an Add-Contact-group action shows for the current mode: in Update mode only the update-safe
   *  actions remain (the create/subscribe action is hidden). */
  public isAddContactActionVisible(appName: string, actionType: string): boolean {
    if (!this.supportsFork(appName) || this.getContactMode(appName) === ContactMode.Add) {
      return true;
    }
    return UPDATE_SAFE_ADD_CONTACT_ACTIONS.includes(actionType);
  }

  private isActionVisible(app: AutomationEditorConfig, group: ActionConfig, actionType: string): boolean {
    if (group.nameGroup === TypeStep.AddContact) {
      return this.isAddContactActionVisible(app.name, actionType);
    }
    return this.isGroupVisibleForMode(app.name, group.nameGroup);
  }

  // Folds the per-app helpers into one view-model so the template binds fields, not method calls. `app` and
  // the nested item/group references stay live (identity preserved) for the reused per-action editors.
  private buildAppCard(app: AutomationEditorConfig): AppCardVm {
    const addGroup = this.addContactGroup(app);
    const groups = this.otherGroups(app);
    const flat = this.rendersFlat(app);
    return {
      app,
      translationName: this.appTranslationName(app),
      adminOnly: this.appAdminOnly(app),
      realTimeLocked: this.isRealTimeLocked(app),
      supportsFork: this.supportsFork(app.name),
      contactMode: this.getContactMode(app.name),
      updateList: this.buildUpdateListVm(app),
      addGroup,
      addActions: (addGroup?.actionsGroup ?? []).map((item) => ({
        item,
        visible: this.isAddContactActionVisible(app.name, item.action.$type),
      })),
      addGroupAdditionalInfoKey: addGroup?.additionalInfoKey,
      addGroupDisabled: addGroup?.disabled === true,
      addGroupDisabledTooltip: addGroup?.disabled === true ? this.missedInputTooltip : '',
      createListMissing: this.hasMissingCreateList(app),
      rendersFlat: flat,
      flatNameGroup: flat ? groups[0]?.nameGroup : undefined,
      flatActions: flat ? (groups[0]?.actionsGroup ?? []) : [],
      flatAdditionalInfoKey: flat ? groups[0]?.additionalInfoKey : undefined,
      widgetStatesContext: this.widgetStatesContext(app),
      groups: groups.map((group) => ({
        group,
        visible: this.isGroupVisibleForMode(app.name, group.nameGroup),
        isZapierEmbed: this.isZapierEmbedGroup(group),
        isZapierTrigger: group.isZapierTrigger === true,
        isTag: this.isTagGroup(group.nameGroup),
        allowCreate: group.nameGroup === TypeStep.TagAdd,
        selectedTag: this.selectedTagOf(group),
        profileSwitch: this.profileSwitchOf(group),
        isUpdateOtherFields: group.nameGroup === TypeStep.UpdateOtherFields,
        additionalInfoKey: group.additionalInfoKey,
        disabled: group.disabled === true,
        disabledTooltip: group.disabled === true ? this.missedInputTooltip : '',
      })),
    };
  }

  private buildUpdateListVm(app: AutomationEditorConfig): AppCardVm['updateList'] {
    if (!this.supportsFork(app.name) || this.getContactMode(app.name) !== ContactMode.Update) {
      return undefined;
    }
    const target = findUpdateListTarget(app);
    if (!target) {
      return undefined;
    }
    const selected = this.updateListByApp()[app.name];
    const hasValue = this.hasListValue(selected);
    const options = this.allowedUpdateListOptions(app.name);
    return {
      labelKey: target.multiple
        ? 'campaign.widget.sidebar.form-settings-v2.update-tags-select'
        : 'campaign.widget.sidebar.form-settings-v2.update-list-select',
      options,
      value: hasValue ? selected : target.multiple ? [] : '',
      multiple: target.multiple,
      hasValue,
      missingLabel: this.missingUpdateListIds(selected, options).join(', '),
    };
  }

  /** Ids the picker no longer offers: it renders blank while still holding them, so the stale value would
   *  be saved again unnoticed. Warned about rather than blocked, matching the per-action editor. */
  private missingUpdateListIds(selected: string | string[] | undefined, options: UpdateListOption[]): string[] {
    if (!this.hasListValue(selected) || !options.length) {
      return [];
    }
    const offered = new Set(options.map((option) => String(option.id)));
    const values = Array.isArray(selected) ? selected : [selected as string];
    return values.filter((value) => !offered.has(String(value)));
  }

  /** Applies the integration's lists-access setting (`listIdsForTrigger`), as selectListIdsFromIntegration
   *  does for the editor's own dropdown. Filtered on read so a late integrations response still narrows an
   *  already-loaded list. No setting means unrestricted; an empty one offers nothing, as in the Add step. */
  private allowedUpdateListOptions(appName: string): UpdateListOption[] {
    const options = this.updateListOptions()[appName] ?? [];
    const allowed = this.listsIntegrations()[appName.toLowerCase()];
    if (!Array.isArray(allowed)) {
      return options;
    }
    return options.filter((option) => allowed.includes(String(option.id)));
  }

  /** List/tag id propagation: when a required-list action gets a value, push it onto sibling actions in the
   *  expanded app and trigger the per-action editors to reload their field options. */
  public onSetPropertyList(event: {
    action?: { nameAction?: string };
    property?: { name?: string; value?: unknown };
  }): void {
    const propName = event?.property?.name;
    if ((propName !== PROPS_TYPE.ListId && propName !== PROPS_TYPE.TagIds) || !event.property?.value) {
      return;
    }
    const listProp = event.action?.nameAction === 'Rule' ? PROPS_TYPE.TagIds : PROPS_TYPE.ListId;
    const app = this.expandedApp();
    app?.actionsConfig.forEach((group) => {
      if (!group.requiredList) {
        return;
      }
      group.actionsGroup.forEach((item) => {
        (item.action as unknown as Record<string, unknown>)[listProp] = event.property?.value;
      });
    });
    // The create action's list belongs to Add mode; re-stamp so Update mode keeps its own selection.
    if (app) {
      this.applyListForMode(app);
    }
    this.proceedRequiredListActions.set(String(event.property.value));
    this.bumpApps();
  }

  public onDynamicMapFieldRealTime(event: { value?: { checked?: boolean } | null; type: string }): void {
    this.modifyMapFieldRealTime(event?.value, event?.type);
  }

  // Removes inputs that cannot be mapped in real time (telephone, and for Voyado social-security) and
  // restores them when real time is turned off.
  private modifyMapFieldRealTime(value: { checked?: boolean } | null | undefined, integrationType: string): void {
    const app = this.editorApps().find((candidate) => candidate.name === integrationType);
    const fieldMappingConfig = this.getFieldMappingProps(app);
    if (!fieldMappingConfig) {
      return;
    }
    if (value?.checked || ALWAYS_REAL_TIME_APPS.includes(integrationType)) {
      this.removedVerifyNewSubscriber = false;
      if (integrationType !== IntegrationType.Rule) {
        ['session.custom.telephone'].forEach((prop) => {
          const index = fieldMappingConfig.findIndex((field) => field.value === `{{${prop}}}`);
          if (index !== -1) {
            const removed = fieldMappingConfig.splice(index, index + 1 < fieldMappingConfig.length ? 2 : 1);
            this.removedPropsMapField.push(...removed);
          }
        });
        if (fieldMappingConfig.some((prop) => prop.isSocialInput) && integrationType === IntegrationType.Voyado) {
          const removedSocial: FieldMapProp[] = [];
          for (let i = 0; i < fieldMappingConfig.length; i++) {
            if (fieldMappingConfig[i].isSocialInput && fieldMappingConfig[i + 1]?.value === 'socialSecurityNumber') {
              const removed = fieldMappingConfig.splice(i, i + 1 < fieldMappingConfig.length ? 2 : 1);
              removedSocial.push(...removed);
              i--;
            }
          }
          this.removedPropsMapField = this.setListUiOnRemovedPropsFromExisting(fieldMappingConfig);
          this.removedPropsMapField.push(...removedSocial);
        }
      }
    } else {
      if (integrationType !== IntegrationType.Rule) {
        this.removedPropsMapField = this.setListUiOnRemovedPropsFromExisting(fieldMappingConfig);
        this.removedPropsMapField = this.setListUiOnDynamicChangedField(this.removedPropsMapField, app);
        fieldMappingConfig.push(...this.removedPropsMapField);
        this.removedPropsMapField = [];
      }
      this.removedVerifyNewSubscriber = this.editorApps().every(
        (candidate) => !AutomationHelpers.isBrevoDotVoyadoRuleRealTime(candidate),
      );
    }
    if (integrationType === IntegrationType.Voyado || integrationType === IntegrationType.Rule) {
      this.widgetAutomationsDataService.isAllowFormMappingPropApplyInitial(app, value?.checked);
    }
  }

  private setListUiOnRemovedPropsFromExisting(fieldMappingConfig: FieldMapProp[]): FieldMapProp[] {
    return this.removedPropsMapField.map((propRemoved) => {
      if (propRemoved.name === PROPS_TYPE.FieldValue && !propRemoved.listUI) {
        return {
          ...propRemoved,
          listUI: fieldMappingConfig.find((prop) => prop.name === PROPS_TYPE.FieldValue && prop.listUI)?.listUI,
        };
      }
      if (propRemoved.name === PROPS_TYPE.FieldName && !propRemoved.listUI) {
        return {
          ...propRemoved,
          listUI: fieldMappingConfig.find((prop) => prop.name === PROPS_TYPE.FieldName && prop.listUI)?.listUI,
        };
      }
      return { ...propRemoved };
    });
  }

  private setListUiOnDynamicChangedField(
    removedPropsMapField: FieldMapProp[],
    app: AutomationEditorConfig | undefined,
  ): FieldMapProp[] {
    this.loadListUi(app);
    return removedPropsMapField.map((propRemoved) => {
      if (propRemoved.name === PROPS_TYPE.FieldName && !propRemoved.listUI) {
        return { ...propRemoved, listUI: this.listUIName ?? [] };
      }
      if (propRemoved.name === PROPS_TYPE.FieldValue && !propRemoved.listUI) {
        return { ...propRemoved, listUI: this.listUIValue ?? [] };
      }
      return { ...propRemoved };
    });
  }

  private loadListUi(app: AutomationEditorConfig | undefined): void {
    if (!app) {
      return;
    }
    const allProps = app.actionsConfig
      .flatMap((group) => group.actionsGroup)
      .flatMap((item) => (item.action?.properties ?? []) as FieldMapProp[]);
    if (!this.listUIName?.length) {
      this.listUIName =
        allProps.find(
          (prop) => prop.name === PROPS_TYPE.FieldName && Array.isArray(prop.listUI) && prop.listUI.length > 0,
        )?.listUI ?? null;
    }
    if (!this.listUIValue?.length) {
      this.listUIValue =
        allProps.find(
          (prop) => prop.name === PROPS_TYPE.FieldValue && Array.isArray(prop.listUI) && prop.listUI.length > 0,
        )?.listUI ?? null;
    }
  }

  /** Whether the expanded app can be saved — at least one meaningful value in a visible group and no required
   *  form-settings field left empty. */
  public canSave(app: AutomationEditorConfig): boolean {
    if (this.savingAppName()) {
      return false;
    }
    if (this.hasMissingRequiredProp(app) || this.hasMissingUpdateList(app) || this.hasMissingCreateList(app)) {
      return false;
    }
    if (this.hasHalfFilledUpdateRow(app)) {
      return false;
    }
    if (this.visibleActions(app).some(actionHasInvalidFormatProp)) {
      return false;
    }
    if (this.isIntegrationLevelDraft(app)) {
      return true;
    }
    return this.hasMeaningfulValue(app);
  }

  public saveBlockedTooltipKey(app: AutomationEditorConfig): string | null {
    if (this.hasHalfFilledUpdateRow(app)) {
      return 'automations.errorMessages.fields-required';
    }
    return this.needsFieldMappingToSave(app) ? 'campaign.widget.sidebar.form-settings-v2.needs-field-mapping' : null;
  }

  /** Update mode saves one action per completed field-mapping row, so a chosen list on its own persists nothing. */
  private needsFieldMappingToSave(app: AutomationEditorConfig): boolean {
    return (
      this.supportsFork(app.name) &&
      this.getContactMode(app.name) === ContactMode.Update &&
      this.hasChosenUpdateList(app) &&
      !this.hasMeaningfulValue(app)
    );
  }

  /** Not `!hasMissingUpdateList`: an integration that updates the contact in place offers no list to
   *  choose, and both predicates read false for it. */
  private hasChosenUpdateList(app: AutomationEditorConfig): boolean {
    return !!findUpdateListTarget(app) && this.hasListValue(this.updateListByApp()[app.name]);
  }

  private hasHalfFilledUpdateRow(app: AutomationEditorConfig): boolean {
    return app.actionsConfig.some(
      (group) =>
        group.nameGroup === TypeStep.UpdateOtherFields &&
        group.actionsGroup.some(
          (item) =>
            this.isActionVisible(app, group, item.action.$type) &&
            hasHalfFilledFieldMappingPair(item.action.properties),
        ),
    );
  }

  // The actions actually shown/editable in the current mode — across the combined Add Contact section and the
  // accordion groups — so validation never counts a hidden create action that won't be saved.
  private visibleActions(app: AutomationEditorConfig): CustomModelAction[] {
    const actions: CustomModelAction[] = [];
    app.actionsConfig.forEach((group) =>
      group.actionsGroup.forEach((item) => {
        if (this.isActionVisible(app, group, item.action.$type)) {
          actions.push(item.action);
        }
      }),
    );
    return actions;
  }

  private hasMeaningfulValue(app: AutomationEditorConfig): boolean {
    if (this.hasCreateListValue(app)) {
      return true;
    }
    return this.visibleActions(app).some(
      (action) =>
        fieldMappingPairs(action.properties).length > 0 ||
        (action.properties ?? []).some((prop) => this.propCountsAsConfigured(prop, action)),
    );
  }

  /** Mapping properties are judged as whole rows above: the source half is pre-filled before the user
   *  touches anything, so counting it alone enables a Save that persists nothing. A tag step's profile
   *  switch is discounted for the same reason: `excludeEmptyTagActions` drops a tag action with no tag. */
  private propCountsAsConfigured(prop: CustomModelProperty, action: CustomModelAction): boolean {
    if (prop.name === PROPS_TYPE.ListId || prop.name === PROPS_TYPE.TagIds || isFieldMappingPropName(prop.name)) {
      return false;
    }
    if (isTagProfileSwitch(action, prop)) {
      return false;
    }
    if (Array.isArray(prop.value)) {
      return prop.value.some((value: unknown) => !!value);
    }
    return (!!prop.value && prop.value !== 'none') || prop.value === false || prop.typeInput === 'description';
  }

  /** Where the create action has nothing but its list, that list is the configuration — unlike an update or
   *  tag step's list, which `propCountsAsConfigured` discounts. Description-typed means real time took it. */
  private hasCreateListValue(app: AutomationEditorConfig): boolean {
    return (this.addContactGroup(app)?.actionsGroup ?? []).some(
      (item) =>
        this.isAddContactActionVisible(app.name, item.action.$type) &&
        (item.action.properties ?? []).some(
          (prop: FieldMapProp & { typeInput?: string }) =>
            isListPropName(prop.name) && (this.hasListValue(prop.value) || prop.typeInput === 'description'),
        ),
    );
  }

  /** The backend rejects an update with no list — Mailchimp answers 400 "ListId is required". */
  private hasMissingUpdateList(app: AutomationEditorConfig): boolean {
    if (!this.supportsFork(app.name) || this.getContactMode(app.name) !== ContactMode.Update) {
      return false;
    }
    if (!findUpdateListTarget(app)) {
      return false;
    }
    return !this.hasListValue(this.updateListByApp()[app.name]);
  }

  /**
   * `serializeSimpleAction` drops a create action whose required list is empty, silently degrading an Add-mode
   * save into an update-only automation. Checked here rather than in `hasMissingRequiredProp`, which waives
   * required-ness whenever the dropdown offers a blank option — right for an optional field mapping, wrong for
   * the list a contact is created in. Real time clears both required flags, correctly waiving this too.
   */
  private hasMissingCreateList(app: AutomationEditorConfig): boolean {
    return (this.addContactGroup(app)?.actionsGroup ?? []).some(
      (item) =>
        this.isAddContactActionVisible(app.name, item.action.$type) &&
        (item.action.properties ?? []).some(
          (prop: FieldMapProp & { required?: boolean; requiredInFormSettings?: boolean }) =>
            isListPropName(prop.name) && this.isCreateListRequired(prop) && !this.hasListValue(prop.value),
        ),
    );
  }

  /** Klaviyo, MailerLite, Emarsys, Custobar and Apsis mark their create list `required` only. */
  private isCreateListRequired(prop: { required?: boolean; requiredInFormSettings?: boolean }): boolean {
    return prop.requiredInFormSettings === true || prop.required === true;
  }

  private hasMissingRequiredProp(app: AutomationEditorConfig): boolean {
    return this.visibleActions(app).some((action) =>
      (action.properties ?? []).some(
        (prop) =>
          (prop.requiredInFormSettings && !prop.value && !prop.addEmptyOption) ||
          (prop.name === 'doubleOptInFormId' && prop.required && !prop.value),
      ),
    );
  }

  // CleverReach add (no per-action inputs) and Custom webhook are configured at the integration level, so for
  // a fresh draft, enabling the app is itself the change.
  private isIntegrationLevelDraft(app: AutomationEditorConfig): boolean {
    if (!app.isDraft) {
      return false;
    }
    return app.actionsConfig.some((group) =>
      group.actionsGroup.some(
        (item) =>
          (item.action.$type === ACTIONS_TYPES.CleverReachCreateContact ||
            item.action.$type === ACTIONS_TYPES.CustomSendWebhook) &&
          (item.action.properties ?? []).length === 0,
      ),
    );
  }

  public saveApp(app: AutomationEditorConfig): void {
    if (!this.canSave(app)) {
      return;
    }
    this.applyListForMode(app);
    // Pre-serialize cleanup: drop 'none' field values and force Voyado real-time on save. It all mutates in
    // place, so it runs on a clone — a failed save must not strip the open panel of the user's input.
    const cleaned = AutomationHelpers.removeNoneProperties(JSON.parse(JSON.stringify(app)) as AutomationEditorConfig);
    if (this.supportsFork(app.name) && this.getContactMode(app.name) === ContactMode.Update) {
      this.clearAddContactCreateActions(cleaned);
    }
    if (this.account) {
      WidgetAutomationHelpers.forceUseRunningInRealTimeForVoyado(cleaned, this.account, true);
    }

    this.savingAppName.set(app.name);
    const { automation, isUpdate, appActionCount } = this.serializer.buildAutomationRequest({
      account: this.account as Account,
      widgetState: this.widgetState,
      widgetStates: this.widget?.states ?? [],
      automationConfig: cleaned,
      existingAutomation: this.existingAutomation,
      campaignName: this.campaignName,
      campaignStatus: this.campaignStatus,
      runAutomationOnlyIfNewSubscriber: false,
      onlySubscriberCheck: false,
      removeVerifyNewSubscriberCheck: this.removedVerifyNewSubscriber || this.updatesExistingContact(),
    });

    // Contributing nothing means canSave and the serializer have drifted; sending it would drop the
    // integration's stored actions behind a success message.
    if (!appActionCount && !this.isIntegrationLevelDraft(app)) {
      this.savingAppName.set(null);
      this.snackBarService.error('campaign.widget.sidebar.form-settings-v2.nothing-to-save');
      return;
    }

    const save$ = isUpdate
      ? this.widgetAutomationsService.updateAutomation(automation)
      : this.widgetAutomationsService.createAutomation(automation);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => this.onSaved(saved),
      error: () => {
        this.savingAppName.set(null);
        this.snackBarService.error('generic.errorOccurred');
      },
    });
  }

  private onSaved(saved: Automation): void {
    this.persistAutomationIdIfNeeded(saved);
    this.existingAutomation = saved;
    this.widgetAutomationsDataService.existingAutomation = saved;
    this.removedPropsMapField = [];
    this.rebuildFromAutomation(saved);
    this.syncRealTimeFormSettings(saved);
    this.refreshGoToAutomation();
    this.setExpandedApp(null);
    this.savingAppName.set(null);
    this.notifyEditorChanged();
    this.snackBarService.success('generic.success');
  }

  // Keep widget.formSettings in step with whether anything runs in real time:
  //  - a freshly real-time integration with no form settings yet gets the default success/error messages;
  //  - removing the last real-time integration drops the now-meaningless handler — but a leaderboard
  //    (formSettings.dataSourceId) keeps its real-time mode, so its form settings are preserved.
  private syncRealTimeFormSettings(saved: Automation): void {
    if (!this.widget) {
      return;
    }
    const hasRealTime = (saved?.actions ?? []).some(
      (action) => (action as { isRunningInRealTime?: boolean }).isRunningInRealTime === true,
    );
    const formSettings = this.widget.formSettings;
    if (hasRealTime && !formSettings) {
      const view = buildHandlerView(null, null, (key) => this.accountLanguageService.translate(key));
      this.persistFormSettings(toFormSettingsPayload(view, undefined));
    } else if (!hasRealTime && !formSettings?.dataSourceId && formSettings?.success) {
      this.persistFormSettings(null);
    }
  }

  private persistFormSettings(payload: FormSettings | null): void {
    if (!this.widget) {
      return;
    }
    this.widget.formSettings = payload ?? undefined;
    this.widgetService
      .patchWidget(this.widget.id, '/formSettings', payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
  }

  private persistAutomationIdIfNeeded(saved: Automation): void {
    if (saved?.id && this.widgetState.automationId !== saved.id) {
      this.widgetState.automationId = saved.id;
      this.widgetEditorDataService
        .updateAutomationIdOnCurrentState(saved.id, this.widgetState)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ error: () => undefined });
    }
  }

  public removeApp(app: AutomationEditorConfig): void {
    // A freshly-enabled, unsaved draft has nothing persisted — just revert it in the UI, no backend call.
    if (app.isDraft || !this.existingAutomation) {
      app.enabled = false;
      app.isDraft = false;
      if (this.expandedAppName() === app.name) {
        this.setExpandedApp(null);
      }
      this.bumpApps();
      return;
    }
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        value: app.name,
        title: 'confirmationDialog.confirmationTitle',
        text: 'campaign.widget.sidebar.form-settings.remove-automation-for-app',
      },
    });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed || !this.existingAutomation) {
          return;
        }
        const without = WidgetAutomationHelpers.removeAllActionsFromAutomationForApp(this.existingAutomation, app);
        this.savingAppName.set(app.name);
        this.widgetAutomationsService
          .updateAutomation(without)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (saved) => this.onSaved(saved),
            error: () => {
              this.savingAppName.set(null);
              this.snackBarService.error('generic.errorOccurred');
            },
          });
      });
  }

  private rebuildFromAutomation(automation: Automation): void {
    const apps = this.serializer.toEditorConfig(automation, this.widget?.states ?? [], this.account as Account);
    this.enrichWithAuthState(apps);
    this.applyCustomIntegrationOverrides(apps);
    this.applyRealTimeStepDisabling(apps);
    this.editorApps.set(apps);
    this.initContactModes(apps);
  }

  private notifyEditorChanged(): void {
    this.widgetService.hasUnpublishedChangesBS.next(true);
    this.flyoutService.integrationSaved$.next(this.widget?.id);
  }

  public closeFlyout(): void {
    this.flyoutService.close();
  }

  /** Footer "go to automation" is shown only when a saved automation is linked and the tier allows it. Set
   *  from non-signal state, so it's refreshed explicitly whenever that state changes. */
  private refreshGoToAutomation(): void {
    this.canGoToAutomation.set(
      !!this.existingAutomation?.id && !!this.widgetState?.automationId && this.tierAllowsAutomations,
    );
  }

  public openAutomationInNewTab(): void {
    const automationId = this.widgetState?.automationId;
    if (automationId) {
      window.open(`/automations/edit/${automationId}`, '_blank');
    }
  }

  /** Empty-state CTA: send the user to the apps settings to connect an integration. */
  public connectApps(): void {
    this.router.navigate(['/settings/apps']);
    this.flyoutService.close();
  }

  // The hosted "show message on submit" section reads this state (the parent owns it privately).
  public get showMessageWidget(): Widget | null {
    return this.widget;
  }
  public get showMessageWidgetState(): WidgetState {
    return this.widgetState;
  }
  public get showMessageAccount(): Account | null {
    return this.account;
  }
  public get showMessageAutomation(): Automation | null {
    return this.existingAutomation;
  }
  public get sectionCampaignName(): string | null {
    return this.campaignName;
  }

  /** Keep the engine's automation copy in sync after the section re-saves it (verify-as-new-subscriber). */
  public onShowMessageAutomationChanged(automation: Automation): void {
    this.existingAutomation = automation;
    this.widgetAutomationsDataService.existingAutomation = automation;
    this.refreshGoToAutomation();
  }

  @ViewChild(FlyoutFormSettingsShowMessageComponent)
  private readonly showMessage?: FlyoutFormSettingsShowMessageComponent;

  /** Selecting a leaderboard writes formSettings.dataSourceId, which can flip the post-submit handling to
   *  real-time — re-evaluate the show-message section's mode. */
  public onLeaderboardChanged(): void {
    this.showMessage?.refreshMode();
  }

  /** Toggling eCommerce registration changes formRegistrationType in place — re-gate/re-filter the
   *  alternating-message section (its `alternativeApps` getter and availability depend on it). */
  public onEcommerceRegistrationChanged(): void {
    this.showMessage?.refreshMode();
  }

  private setExpandedApp(name: string | null): void {
    this.expandedAppName.set(name);
    // Steps collapse on re-render; keep the Zapier embed's lazy-load flag in step.
    this.zapierEmbedExpanded.set(false);
  }

  /** The per-action editors append their dropdowns to the body, so an option click never bubbles through here. */
  private watchEditorEdits(): void {
    merge(
      fromEvent(document, 'input'),
      fromEvent(document, 'change'),
      fromEvent(document, 'click'),
      fromEvent(document, 'keyup'),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.editorEdits.update((count) => count + 1));
  }

  private expandedApp(): AutomationEditorConfig | undefined {
    const name = this.expandedAppName();
    return name ? this.editorApps().find((app) => app.name === name) : undefined;
  }

  private getFieldMappingProps(app: AutomationEditorConfig | undefined): FieldMapProp[] | undefined {
    return app?.actionsConfig.find((group) => group.nameGroup === TypeStep.FieldMapping)?.actionsGroup[0]?.action
      ?.properties as FieldMapProp[] | undefined;
  }

  private bumpApps(): void {
    // New array ref (same app object refs) so the configured/non-configured computeds recompute while the
    // reused per-action editors keep their in-place property edits.
    this.editorApps.update((apps) => [...apps]);
  }

  private viewName(type: string): string {
    return type === IntegrationType.RuleMailer ? IntegrationType.Rule : type;
  }

  // Update mode must not create a contact: clear the create/subscribe action(s) in the Add Contact group so
  // they aren't serialized, while leaving update-safe actions (e.g. Voyado consent) intact so they're saved.
  private clearAddContactCreateActions(app: AutomationEditorConfig): void {
    const addGroup = this.addContactGroup(app);
    if (!addGroup) {
      return;
    }
    // CleverReach's add action has no properties, so blanking wouldn't distinguish it from a real Add —
    // `serializeSimpleAction` always serializes it. Drop it outright instead.
    addGroup.actionsGroup = addGroup.actionsGroup.filter(
      (item) => item.action.$type !== ACTIONS_TYPES.CleverReachCreateContact,
    );
    addGroup.actionsGroup.forEach((item) => {
      if (UPDATE_SAFE_ADD_CONTACT_ACTIONS.includes(item.action.$type)) {
        return;
      }
      const action = item.action as unknown as Record<string, unknown>;
      ((action['properties'] as Array<{ value: unknown }>) ?? []).forEach((prop) => {
        prop.value = Array.isArray(prop.value) ? [] : '';
      });
      // Left set, it revives this action in forceUseRunningInRealTimeForVoyado and re-adds the "new subscriber" trigger.
      action['isRunningInRealTime'] = false;
      action['listId'] = '';
      action['listIds'] = '';
      action['tagId'] = '';
      action['tagIds'] = [];
      action['fieldMappings'] = undefined;
    });
  }
}
